// Angular
import { Inject, Injectable, Injector, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
// RxJS
import { BehaviorSubject, fromEvent, merge, Observable, Subject } from 'rxjs';
import { filter, map, mapTo, shareReplay, takeUntil } from 'rxjs/operators';
//Service
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Store } from '@ngxs/store';
import { ConfigService } from '../service/config.service';
import { BroadcastService } from '../service/broadcast.service';
import { ErrorFactoryService } from '../service/error-factory.service';
// Models
import {
	HcCloseEvent,
	HcGuideCloseEvent,
	HcGuideOpenEvent,
	HcMockData,
	HcOpenEvent,
	HcRequestErrorEvent,
	HcRequestSubmitEvent,
	HcSectionsConfig,
} from './model/help-center-events.model';
import { ConfigFileModel } from '../model/config-file.model';
import { HelpCenterAppContext, PatchContextModel } from '../model/app-functions.model';
import { UserContextState } from '../auth/user-context.state';
import { UserContextModel } from '../auth/user-context.model';
import { ERROR_TYPE } from '../error-track/model/error-code.model';
import { GlobalUtils } from '../utils/global.utils';

/** Tag name of the web component registered in index.html */
const WIDGET_TAG = 'help-center-widget';

@Injectable({ providedIn: 'root' })
export class HelpCenterService implements OnDestroy {

	// ---- Public event streams -----------------------------------------------

	/** Fires when the Help Center panel opens */
	readonly panelOpen$: Observable<HcOpenEvent>;

	/** Fires when the Help Center panel closes */
	readonly panelClose$: Observable<HcCloseEvent>;

	/** Fires when a PDF guide dialog is opened */
	readonly guideOpen$: Observable<HcGuideOpenEvent>;

	/** Fires when the PDF guide dialog is closed */
	readonly guideClose$: Observable<HcGuideCloseEvent>;

	/** Fires when a support request is submitted successfully */
	readonly requestSubmit$: Observable<HcRequestSubmitEvent>;

	/** Fires when a support request submission fails */
	readonly requestError$: Observable<HcRequestErrorEvent>;

	/**
	 * Current open/closed state of the panel.
	 * Emits `true` when open, `false` when closed.
	 * Replays the last known value to late subscribers.
	 */
	readonly isOpen$: Observable<boolean>;

	private readonly _isHelpdeskEnabled$ = new BehaviorSubject(false);
	readonly isHelpdeskEnabled$ = this._isHelpdeskEnabled$.asObservable();

	private readonly _destroy$ = new Subject<void>();
	private _originalFetch: typeof window.fetch | null = null;

	constructor(
		@Inject(DOCUMENT) private readonly _document: Document,
		private configService: ConfigService,
		private store: Store,
		private oidcService: OidcSecurityService,
		private broadcastService: BroadcastService,
		private errorFactoryService: ErrorFactoryService
	) {
		this.panelOpen$    = this._event$<HcOpenEvent>('hc:open');
		this.panelClose$   = this._event$<HcCloseEvent>('hc:close');
		this.guideOpen$    = this._event$<HcGuideOpenEvent>('hc:guide-open');
		this.guideClose$   = this._event$<HcGuideCloseEvent>('hc:guide-close');
		this.requestSubmit$ = this._event$<HcRequestSubmitEvent>('hc:request-submit');
		this.requestError$  = this._event$<HcRequestErrorEvent>('hc:request-error');

		this.isOpen$ = merge(
			this.panelOpen$.pipe(mapTo(true)),
			this.panelClose$.pipe(mapTo(false)),
		).pipe(
			shareReplay(1),
		);

		this.configService.getConfig().subscribe(async (configFile: ConfigFileModel): Promise<void> => {
			if (!GlobalUtils.isInsideIframe() && configFile?.helpdesk?.api){
				this.setApiBaseURL(configFile.helpdesk.api);
				// Try to set token immediately if available
				this._resolveAndSetToken();
			}
		});

		this.setReloadAppContext(async () => {
			const userContext: UserContextModel = this.store.selectSnapshot(UserContextState.getUserContext);
			const lastError = this.errorFactoryService.retrieveLatestCodeErrors([ERROR_TYPE.REQUEST, ERROR_TYPE.LOG], true, false);

			this.patchContext({
				user: userContext.id,
				timestamp: new Date().toISOString(),
				lastError: lastError
			});
		});

		// Listen for token availability after login
		this.store.select(UserContextState.token).pipe(
			filter((token: any) => !!token),
			takeUntil(this._destroy$)
		).subscribe((token: any) => {
			if(token){
				this.setAuthToken(token);
			}
		});

		// Listen for custom actions from the navbar
		this.broadcastService.onEvent('HELPDESK_CUSTOM_ACTION')
			.pipe(takeUntil(this._destroy$))
			.subscribe((broadcast: any) => {
				const action = broadcast.event;
				switch (action) {
					case 'OPEN_HELPDESK':
						this.open()
						break;
					case 'CLOSE_HELPDESK':
						this.close();
						break;
					default:
						console.log('[HelpCenterService] Unknown custom action:', action);
						break;
				}
			});
	}

	// ---- is Help Desk Enabled ------------------------------------------------

	get isHelpdeskEnabled(): boolean {
		return this._isHelpdeskEnabled$.value;
	}

	set isHelpdeskEnabled(value: boolean) {
		this._isHelpdeskEnabled$.next(value);
	}

	// ---- Panel control -------------------------------------------------------

	/** Opens the Help Center panel */
	open(): void {
		this._widget?.open();
	}

	/** Closes the Help Center panel */
	close(): void {
		this._widget?.close();
	}

	/**
	 * Attempts to resolve the auth token immediately from OidcSecurityService or the store.
	 * If a token is available, sets it on the widget right away.
	 */
	private _resolveAndSetToken(): void {
		try {
			let token: any = this.oidcService.getAccessToken();
			if (!token) {
				token = this.store.selectSnapshot(UserContextState.token);
			}
			if (token) {
				this.setAuthToken(token);
			}
		} catch (_) {
			// Token not available yet — will be set by the store subscription
		}
	}

	/** Toggles the Help Center panel open/closed */
	toggle(): void {
		const widget = this._widget;
		if (!widget) return;
		widget._isOpen ? widget.close() : widget.open();
	}

	// ---- Attribute setters --------------------------------------------------

	/**
	 * Updates `app-context` on the widget with a full HelpCenterAppContext object.
	 * Call this whenever the user navigates or relevant session data changes.
	 *
	 * @param {HelpCenterAppContext} ctx - The complete context object to set.
	 */
	setContext(ctx: HelpCenterAppContext): void {
		this._widget?.setContext(ctx);
	}

	/**
	 * Merges partial data into the current `app-context` without replacing the whole object.
	 *
	 * @param {Partial<HelpCenterAppContext>} patch - Partial context data to merge.
	 */
	patchContext(patch: Partial<PatchContextModel>): void {
		const model = new HelpCenterAppContext(patch);
		this._widget?.patchContext(model);
		if(patch?.closePanel){
			this.close();
		}
	}

	/**
	 * Returns the current app-context from the widget as a parsed object.
	 *
	 * @returns {HelpCenterAppContext | null} The current context or null if widget not available.
	 */
	getContext(): HelpCenterAppContext | null {
		return this._widget?.getContext() || null;
	}

	/**
	 * Overrides the `app-id` attribute (rarely needed — usually set once in index.html).
	 */
	setAppId(appId: string): void {
		this._widget?.setAttribute('app-id', appId);
	}

	/**
	 * Overrides the `api-base-url` attribute.
	 */
	setApiBaseURL(apiBaseUrl:string): void {
		this._widget?.setAttribute('api-base-url', apiBaseUrl);
	}

	setReloadAppContext(fn: (ctx: any) => Promise<void>): void {
		const widget = this._widget;
		if (widget) widget.reloadAppContext = fn;
	}

	/**
	 * Sets the `auth-token` attribute on the widget so it can include
	 * the Bearer token in API requests.
	 */
	setAuthToken(token: string): void {
		if (token) {
			this._widget?.setAttribute('auth-token', token);
		}
	}

	/**
	 * Enables or disables individual sections of the Help Center panel.
	 * Only the keys provided are affected; omitted keys keep their current state.
	 *
	 * @example — hide Submit Request, keep the rest visible
	 * this.helpCenter.setSections({ request: false });
	 *
	 * @example — show only FAQ
	 * this.helpCenter.setSections({ guidelines: false, faq: true, request: false });
	 */
	setSections(config: Partial<HcSectionsConfig>): void {
		const widget = this._widget;
		if (!widget) return;

		const attrMap: Record<keyof HcSectionsConfig, string> = {
			guidelines: 'disable-guidelines',
			faq: 'disable-faq',
			request: 'disable-request',
		};

		(Object.keys(config) as Array<keyof HcSectionsConfig>).forEach(key => {
			const attr = attrMap[key];
			if (config[key]) {
				widget.removeAttribute(attr);
			} else {
				widget.setAttribute(attr, '');
			}
		});
	}

	// ---- Mock mode ----------------------------------------------------------

	/**
	 * Overrides `window.fetch` to intercept widget API calls and return
	 * in-memory data. Useful for local development or Storybook when no
	 * backend is available.
	 *
	 * The base URL intercepted is taken from the widget's `api-base-url`
	 * attribute at call time, so call this after the widget is in the DOM.
	 *
	 * @example
	 * this.helpCenter.enableMockMode({
	 *   guidelines: [{ id: '1', title: 'Comp Grid Guide', pdfUrl: '/assets/guide.pdf' }],
	 *   categories: [{ id: 'general', name: 'General' }],
	 *   faqItems: {
	 *     general: [{ id: '1', question: 'How do I...?', answer: 'You can...' }],
	 *   },
	 * });
	 */
	enableMockMode(data: HcMockData): void {
		if (this._originalFetch) return; // already active

		const apiBase = (this._widget?.getAttribute('api-base-url') ?? '/api/help').replace(/\/$/, '');
		this._originalFetch = window.fetch.bind(window);
		const realFetch = this._originalFetch;

		window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
			const url = typeof input === 'string' ? input : (input as Request).url;

			if (url.includes(`${apiBase}/guidelines`)) {
				return this._mockJson(data.guidelines ?? []);
			}

			if (url.includes(`${apiBase}/faq/categories`)) {
				return this._mockJson(data.categories ?? []);
			}

			if (url.includes(`${apiBase}/faq/items`)) {
				const catId = new URL(url, location.href).searchParams.get('categoryId') ?? '';
				return this._mockJson((data.faqItems ?? {})[catId] ?? []);
			}

			if (url.includes(`${apiBase}/requests`)) {
				return this._mockJson({ success: true });
			}

			return realFetch(input, init);
		};
	}

	/** Restores `window.fetch` to its original implementation. */
	disableMockMode(): void {
		if (!this._originalFetch) return;
		window.fetch = this._originalFetch;
		this._originalFetch = null;
	}

	// ---- Lifecycle ----------------------------------------------------------

	ngOnDestroy(): void {
		this.disableMockMode();
		this._destroy$.next();
		this._destroy$.complete();
	}

	// ---- Private helpers ----------------------------------------------------

	private get _widget(): any {
		return this._document.querySelector(WIDGET_TAG);
	}

	/**
	 * Returns a typed Observable that emits the `detail` of every CustomEvent
	 * with the given name, filtered to events originating from the widget.
	 * Events are shared so multiple subscribers reuse one DOM listener.
	 */
	private _event$<T>(eventName: string): Observable<T> {
		return fromEvent<CustomEvent>(this._document, eventName).pipe(
			filter(e => (e.target as Element)?.tagName?.toLowerCase() === WIDGET_TAG),
			map(e => e.detail as T),
			takeUntil(this._destroy$),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
	}

	private _mockJson(body: unknown): Response {
		return new Response(JSON.stringify(body), {
			status: 200,
			headers: { 'Content-Type': 'application/json' },
		});
	}

	private _parseContext(raw: string | null): Record<string, unknown> {
		if (!raw) return {};
		try {
			return JSON.parse(raw);
		} catch {
			return {};
		}
	}
}
